import React, { useEffect, useRef, useState } from 'react';
import Close from '@mui/icons-material/Close';
import * as S from './Navigation.styled';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  selectIsStaySignedIn,
  selectPassword,
  selectRooms,
  selectUserEmail
} from 'pages/Hospitality/redux';
import { toast } from 'utils/toast';
import {
  selectToken,
  selectLiveView,
  selectPermission,
  useCreds,
} from 'redux/global';
import { dismissHtmlBootSplashWhenContentReady, ensureHtmlBootSplash, isBootSplashFinished, shouldUseLogoSplash } from 'pages/components/Loader/BootSplash';
import { markBootReveal } from 'pages/components/Loader/bootReveal';
import Loader from 'pages/components/Loader/Loader';
import { getRouteLoadingMessage } from 'pages/components/Loader/getRouteLoadingMessage';
import { getHotel, hotelFromGetHotelData, verifyAuth } from './redux';
import { purgeHotelDbCache } from 'utils/dbIndexedCache';
import { moveSideBar } from './utils';
import {
  selectDebugText,
  selectHotelData,
  selectIsSyncCORooms,
  selectLayoutRoomChargePanel,
  selectModalParts,
  selectRightBarDismissTick,
  selectSocket,
  setHotelData,
  setIsSyncCORooms,
  setLayoutRoomChargePanel,
  ModalParts,
} from 'pages/redux';
import { useHistory, useLocation } from 'react-router-dom';
import Modal from 'pages/components/Modal/Modal';
import Footer from './Footer/Footer';
import { lFColor2, mobileRes } from 'pages/components/LFHeader/LFHeader.styled';
import { useFetchLogs, useWindowSize } from 'pages/Logs/hooks';
import { useCX, usePath } from 'pages/hooks';
import CheckoutRoomChargeAlert, {
  isCheckoutRoomChargeAlertActive,
  readLiveDismissedRoomChargeIds,
} from './components/CheckoutRoomChargeAlert';
import LogsCapacityAlert from './components/LogsCapacityAlert';
import ManagerReportsAlert, {
  isManagerReportsAlertActive,
} from './components/ManagerReportsAlert';
import LayoutAlertPresence from './components/LayoutAlertPresence';
import { useParentEffects } from './hooks';
import { selectManagerReports, fetchManagerReports } from 'pages/Reporting/redux';
import SignInModal from 'pages/SignIn/modals/SignInModal';
import { useFetchSettings, useSettings } from 'pages/Settings/hooks';
import LFHeader from 'pages/components/LFHeader/LFHeader';
import MobileLogSearchBar from 'pages/Logs/components/MobileLogSearchBar';
import { useFetchRooms } from 'pages/Hospitality/hooks';
import { selectSettingsParts } from 'pages/Settings/redux';
import RightBar from 'pages/components/RightBar/RightBar';
import RoomChargesPanel from 'pages/Reservations/components/RoomChargesPanel';
import PageEnter from 'pages/components/PageEnter/PageEnter';
import { logButtonClick } from 'utils/analytics';
import { redirectLiveDemoToTry, shouldExitLiveDemoToTry } from 'utils';
import {
  isErpAppPath,
  isErpPagePath,
  isHospitalityPagePath,
} from 'pages/Layout/hospitalityModulePaths';

/** Session-only — live demo banner returns on the next sign-in. */
const LIVE_BANNER_DISMISS_KEY = 'session_dismissed_live_view_banner';

const readLiveBannerDismissed = () => {
  try {
    return sessionStorage.getItem(LIVE_BANNER_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

const Navigation = function Navigation({ children }:{ children: any}) {
  const [loading, setLoading] = useState<boolean>(false);
  const [isShowSignIn, setIsShowSignIn] = useState<boolean>(false);
  const [currentClient, setCurrentClient] = useState<string | number>('');
  const [sessionBootComplete, setSessionBootComplete] = useState(false);
  const isSyncCORooms = useAppSelector(selectIsSyncCORooms);
  const isStaySignedIn = useAppSelector(selectIsStaySignedIn);
  const [hasSyncedUnSynced, setHasSyncedUnSynced] = useState(false);
  const email = useAppSelector(selectUserEmail);
  const password = useAppSelector(selectPassword);
  const token = useAppSelector(selectToken);
  const liveView = useAppSelector(selectLiveView);
  const [liveBannerDismissed, setLiveBannerDismissed] = useState(readLiveBannerDismissed);
  const debugText = useAppSelector(selectDebugText);
  const modalParts = useAppSelector(selectModalParts);
  const hotelData = useAppSelector(selectHotelData);
  const socket = useAppSelector(selectSocket);
  const settingsParts = useAppSelector(selectSettingsParts);
  const rooms = useAppSelector(selectRooms);
  const permission = useAppSelector(selectPermission);
  const managerReports = useAppSelector(selectManagerReports);
  const layoutRoomChargePanel = useAppSelector(selectLayoutRoomChargePanel);
  // Re-read session mock settles when pay bumps the tick (frees top strip)
  const rightBarDismissTick = useAppSelector(selectRightBarDismissTick);
  const [layoutRightBarModal, setLayoutRightBarModal] = useState<ModalParts | null>(null);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  useFetchLogs();
  useFetchRooms();
  useFetchSettings();
  const { isHomePath } = usePath();
  // Re-read session mock settles when pay bumps the tick (frees top strip)
  const sessionDismissedIds = rightBarDismissTick >= 0
    ? readLiveDismissedRoomChargeIds()
    : undefined;
  const roomChargeAlertActive = isCheckoutRoomChargeAlertActive(
    rooms,
    permission,
    isHomePath,
    Date.now(),
    sessionDismissedIds,
    liveView,
  );
  const managerReportsAlertActive = isManagerReportsAlertActive(
    managerReports,
    isHomePath,
    email,
  );
  const {
    logout, expireCreds, login, otherTabLogin
  } = useCreds();
  const { COSync, CORoomSync, isNoCOPermission } = useCX();
  const { rates, roomTypes } = useSettings();
  const { width: wWidth } = useWindowSize();
  useParentEffects();

  // Anyone with an inbox (reports or responses) loads items for the top alert.
  useEffect(() => {
    if (!token) return;
    dispatch(fetchManagerReports());
  }, [token, hotelData?.id, dispatch]);

  useEffect(() => {
    if (layoutRoomChargePanel) {
      setLayoutRightBarModal({
        component: (
          <RoomChargesPanel
            selectedRoomId={layoutRoomChargePanel.roomId}
            bookingId={layoutRoomChargePanel.bookingId}
            mockSessionSettle={layoutRoomChargePanel.mockSessionSettle}
          />
        ),
      });
    } else {
      setLayoutRightBarModal(null);
    }
  }, [layoutRoomChargePanel]);

  const setLayoutRightBarModal0 = (m: ModalParts | null) => {
    setLayoutRightBarModal(m);
    if (!m) {
      dispatch(setLayoutRoomChargePanel(null));
    }
  };

  useEffect(() => {
    const metaTags: { name: string; content: string }[] = [
      { name: 'theme-color', content: lFColor2 },
      { name: 'msapplication-navbutton-color', content: lFColor2 },
      { name: 'apple-mobile-web-app-status-bar-style', content: lFColor2 },
    ];
    metaTags.forEach(({ name, content }) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    });
  }, []);

  const authInFlightRef = useRef(false);

  const authorizeAndLogin = () => {
    const loginUser = async () => {
      setLoading(true);
      const hotelId = await dispatch(login({
        email: email.toLowerCase(),
        password,
        hotelId: localStorage.getItem('hotel_data_id') || undefined,
      }));
      if (hotelId) {
        await purgeHotelDbCache(String(hotelId));
        const res = await dispatch(getHotel(hotelId, true));
        setLoading(false);
        authInFlightRef.current = false;
        if (res.status === 'success') {
          const hotel = hotelFromGetHotelData(res.data);
          if (hotel) {
            dispatch(setHotelData(hotel));
            return;
          }
        }
        dispatch(logout());
        toast(res.data, { type: 'error' });
        setIsShowSignIn(true);
        return;
      }
      setLoading(false);
      authInFlightRef.current = false;
      dispatch(logout());
      toast('Could not log in', { type: 'error' });
      setIsShowSignIn(true);
    };

    const fetchHotelAndExpireCreds = async (credsExpiry: number) => {
      const res = await dispatch(getHotel(localStorage.getItem('hotel_data_id') || ''));

      setLoading(false);
      authInFlightRef.current = false;
      if (res.status === 'success') {
        const hotel = hotelFromGetHotelData(res.data);
        if (hotel) {
          dispatch(setHotelData(hotel));
          dispatch(expireCreds(credsExpiry));
          return;
        }
      }
      dispatch(logout());
      if (isStaySignedIn) {
        loginUser();
        return;
      }
      toast(res.data, { type: 'error' });
      setIsShowSignIn(true);
    };

    const authorize = async () => {
      setLoading(true);
      const res = await dispatch(verifyAuth());
      if (res.status === 'success') {
        // Keep loading true through hotel fetch so the session loader does not blink.
        await fetchHotelAndExpireCreds(res.data as number);
        return;
      }
      setLoading(false);
      authInFlightRef.current = false;
      if (isStaySignedIn) {
        loginUser();
      } else {
        dispatch(logout());
        setIsShowSignIn(true);
      }
    };

    if (localStorage.getItem('token')) {
      authorize();
    } else {
      authInFlightRef.current = false;
      dispatch(logout());
      setIsShowSignIn(true);
    }
  };

  // Re-read after login; reset when live demo session ends so next sign-in shows it
  useEffect(() => {
    if (!liveView) {
      setLiveBannerDismissed(false);
      return;
    }
    setLiveBannerDismissed(readLiveBannerDismissed());
  }, [liveView]);

  const dismissLiveBanner = () => {
    try {
      sessionStorage.setItem(LIVE_BANNER_DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setLiveBannerDismissed(true);
    logButtonClick('Dismiss live demo banner', { page: 'navigation' });
  };

  useEffect(() => {
    if (isSyncCORooms) {
      dispatch(setIsSyncCORooms(false));
      CORoomSync?.();
    }
  }, [hotelData?.coId]);

  useEffect(() => {
    if (hotelData && roomTypes.length && rates.length && !hasSyncedUnSynced) {
      setHasSyncedUnSynced(true);

      if (!isNoCOPermission) {
        COSync(true);
      }
    }
  }, [hotelData, roomTypes, rates, hasSyncedUnSynced]);

  const startNewSession = () => {
    const hotelIdLs = localStorage.getItem('hotel_data_id');
    const hasToken = Boolean(localStorage.getItem('token'));

    // Socket join — independent of hotel fetch (socket can connect later).
    if (socket && hotelIdLs && currentClient !== hotelIdLs) {
      socket.emit('join_room', `room${hotelIdLs}`);
      setCurrentClient(hotelIdLs);
    }

    // Restore hotel session even if socket is not ready yet.
    // (Previously gated on socket → React 18 race left CRM stuck on loader.)
    if (!hotelData && hasToken && !authInFlightRef.current) {
      authInFlightRef.current = true;
      authorizeAndLogin();
    }
  };

  const isFeedbackScreen = location.pathname.includes('feedback/1/');
  // Public auth pages must not restore a prior session or show the app boot loader
  const isAuthExemptPath = isFeedbackScreen
    || location.pathname.includes('/register')
    || location.pathname.includes('/forgot')
    || location.pathname.includes('/live');

  useEffect(() => {
    if (!isShowSignIn && !isAuthExemptPath) {
      startNewSession();
    }
  }, [isShowSignIn, hotelData, socket, currentClient, isAuthExemptPath]);

  useEffect(() => {
    if (!token) {
      // Live demo ended / expired — leave app sign-in for marketing /try (/live exempt).
      if (shouldExitLiveDemoToTry() && !location.pathname.includes('/live')) {
        redirectLiveDemoToTry();
        return;
      }
      setIsShowSignIn(true);
    }
  }, [token, location.pathname]);

  useEffect(() => {
    localStorage.setItem('room_search', '');
    localStorage.setItem('book_search', '');

    const otherTabOperations = (e: any) => {
      if (e.key === 'logout_time') {
        dispatch(logout(true));
        // Cross-tab live-demo logout → marketing /try (same as settings Sign out).
        if (shouldExitLiveDemoToTry()) {
          redirectLiveDemoToTry();
          return;
        }
        history.push('/');
      }
      if (e.key === 'login_time') {
        dispatch(otherTabLogin());
        setIsShowSignIn(false);
        setCurrentClient('');
      }
    };
    window.addEventListener('storage', otherTabOperations);

    return () => {
      window.removeEventListener('storage', otherTabOperations);
    };
  }, []);

  const isFitScreen = isErpAppPath(location.pathname)
    || location.pathname.includes('feedback/1/');

  const isShowSignInMain = isShowSignIn && !isHomePath;
  const sessionReady = Boolean(hotelData && settingsParts.users && !loading);
  const path = location.pathname;
  const pageDataReady = (() => {
    if (!token || isHomePath || isAuthExemptPath) return true;
    if (!sessionReady) return false;
    // Logs/metrics/dashboard use their own in-page loaders — do not gate the
    // session shell on isLoadingLogs (it flickers as logs/branch-files merge).
    if (
      isHospitalityPagePath(path, 'operations')
      || isHospitalityPagePath(path, 'reservations')
      || isHospitalityPagePath(path, 'reserve')
      || isErpPagePath(path, 'settings')
      || isHospitalityPagePath(path, 'rooms')
    ) {
      return rooms != null;
    }
    return true;
  })();
  // Latch: once the session has painted ready once, never re-show the shell
  // loader until logout. Stops 1–2 reload blinks from readiness flicker.
  useEffect(() => {
    if (!token) {
      setSessionBootComplete(false);
      return;
    }
    if (sessionReady && pageDataReady) {
      setSessionBootComplete(true);
    }
  }, [token, sessionReady, pageDataReady]);

  const sessionBooting = !isAuthExemptPath && !isHomePath && Boolean(token)
    && !sessionBootComplete
    && (!sessionReady || !pageDataReady);
  // Logo splash only after sign-in; refresh uses the in-app loader.
  const useLogoSplash = shouldUseLogoSplash() && !isBootSplashFinished();
  const showLogoSplash = useLogoSplash && sessionBooting;
  const showAppSessionLoader = !useLogoSplash && sessionBooting;
  const holdChildren = showLogoSplash || showAppSessionLoader;

  const wasBootingRef = useRef(true);
  if (holdChildren) {
    wasBootingRef.current = true;
  } else if (wasBootingRef.current) {
    wasBootingRef.current = false;
    markBootReveal();
  }

  useEffect(() => {
    if (showLogoSplash) {
      ensureHtmlBootSplash();
      return undefined;
    }
    if (useLogoSplash && !sessionBooting) {
      const outer = window.requestAnimationFrame(() => {
        dismissHtmlBootSplashWhenContentReady();
      });
      return () => window.cancelAnimationFrame(outer);
    }
    return undefined;
  }, [showLogoSplash, useLogoSplash, sessionBooting]);

  return (
    <S.MainContainer>
      {
        modalParts && (
          <Modal
            component={modalParts.component}
            exitOnBgClick={modalParts.exitOnBgClick}
            noBg={modalParts.noBg}
            skipSideBar={modalParts.skipSideBar}
          />
        )
      }
      {isHomePath && <LFHeader />}
      <>
        {
          isShowSignInMain ? (
            <Modal
              component={(
                <SignInModal onExit={() => setIsShowSignIn(false)} />
              )}
            />
          ) : (
            <>
              <LayoutAlertPresence
                open={
                  liveView
                  && !isHomePath
                  && !roomChargeAlertActive
                  && !managerReportsAlertActive
                  && !liveBannerDismissed
                }
              >
                <S.LiveViewBanner role="status">
                  Live demo · Summit Lodge is view-only — sign up free to edit your own property
                  <S.LiveViewBannerDismiss
                    type="button"
                    aria-label="Dismiss"
                    onClick={dismissLiveBanner}
                  >
                    <Close fontSize="small" />
                  </S.LiveViewBannerDismiss>
                </S.LiveViewBanner>
              </LayoutAlertPresence>
              <ManagerReportsAlert />
              <CheckoutRoomChargeAlert />
              <LogsCapacityAlert />
              {isErpPagePath(location.pathname, 'metrics') && (
                <MobileLogSearchBar
                  breakpoint={mobileRes}
                  aboveHeader
                />
              )}
              {(wWidth || 1051) <= +mobileRes.replace('px', '') && !isHomePath && <LFHeader />}
              {showAppSessionLoader ? <Loader message={getRouteLoadingMessage(path)} /> : null}
              <S.Container id="main_cont" isFitScreen={isFitScreen}>
                <S.Page>
                  <S.DebugBg id="debug_bg">
                    {
                      debugText.map((d, i) => <div key={`debug_${i}`}>{d}</div>)
                    }
                  </S.DebugBg>
                  <S.Bg
                    id="main_bg"
                    onClick={() => {
                      moveSideBar(true);
                    }}
                  />
                  <S.BottomRightPanel isFitScreen={isFitScreen || isHomePath}>
                    {/* Keep routes mounted during session boot so lazy chunks load
                        under the overlay — nulling children forced a second Loader
                        from Suspense after the session loader went away. */}
                    <PageEnter>
                      {children}
                    </PageEnter>
                  </S.BottomRightPanel>
                </S.Page>
                {
                  (isHomePath) && <Footer />
                }
              </S.Container>
              {layoutRightBarModal ? (
                <RightBar
                  modal={layoutRightBarModal}
                  setModal={setLayoutRightBarModal0}
                />
              ) : null}
            </>
          )
        }
      </>
    </S.MainContainer>
  );
};

export default Navigation;
